import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Publish as PublishIcon,
  StopCircle as StopIcon,
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';
import { announcementsAPI, SystemAnnouncement } from '../services/api';

const SystemAnnouncementAdmin: React.FC = () => {
  const { isSuperAdmin } = usePermissions();
  const [current, setCurrent] = useState<SystemAnnouncement | null>(null);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadCurrent = async () => {
    setLoading(true);
    setError(null);
    try {
      const announcement = await announcementsAPI.getCurrent();
      setCurrent(announcement);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore nel caricamento dell\'annuncio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadCurrent();
    }
  }, [isSuperAdmin]);

  const handlePublish = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await announcementsAPI.publish(title.trim(), message.trim());
      setTitle('');
      setMessage('');
      setSuccess('Annuncio pubblicato. Tutti gli utenti lo vedranno al prossimo accesso all\'app.');
      await loadCurrent();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore nella pubblicazione');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await announcementsAPI.deactivateCurrent();
      setSuccess('Annuncio disattivato.');
      await loadCurrent();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore nella disattivazione');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Solo il super admin può gestire gli annunci globali.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <CampaignIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">
          Annunci globali
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Pubblica un messaggio informativo che compare come popup a tutti gli utenti autenticati
        finché non dichiarano di aver preso visione.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Annuncio attivo
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : current ? (
            <>
              <Typography variant="subtitle1" fontWeight="bold">
                {current.title}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1, mb: 2 }}>
                {current.message}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Presa visione: {current.ack_count ?? 0} utenti · Pubblicato il{' '}
                {new Date(current.created_at).toLocaleString('it-IT')}
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<StopIcon />}
                onClick={handleDeactivate}
                disabled={saving}
              >
                Disattiva annuncio
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nessun annuncio attivo.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pubblica nuovo annuncio
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pubblicando un nuovo messaggio, quello precedente viene disattivato automaticamente.
            Tutti gli utenti (anche chi aveva già preso visione del precedente) vedranno il nuovo popup.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label="Titolo"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 200 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Messaggio"
            fullWidth
            multiline
            minRows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
            onClick={handlePublish}
            disabled={saving || !title.trim() || !message.trim()}
          >
            Pubblica
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SystemAnnouncementAdmin;
